import React, { FunctionComponent, useEffect, useMemo } from "react";
import {
    ButtonProps,
    ButtonRenderProps,
    Checkbox,
    ClearFiltersButton,
    ContentSection,
    DetailsRowProps,
    DropdownMultiselect,
    Grid,
    GridColumn,
    GridRow,
    ListTable,
    RenderFunction,
    SearchBox,
    Stack,
    TableFilter
} from "sprint-common-web";

import { TagElementsList } from "../../../common/components/TagElementsList";
import { ContentSubtype } from "../../../common/types/ContentSubtype";
import { MovieContentSubtype } from "../../../common/types/MovieContentSubtype";
import { HeaderBreadcrumb } from "../../../common/ui/layout/header/header-breadcrumb/HeaderBreadcrumb";
import { PageHeader } from "../../../common/ui/layout/header/PageHeader";
import { renderNotMergableRow } from "../../common/utils/renderNotMergableRow";
import { contentManageRoutes } from "../../ContentRoutes";
import { AddMovieCommandBarButton } from "./buttons/AddMovieCommandBarButton";
import { MergeMoviesCommandBarButton } from "./buttons/MergeMoviesCommandBarButton";
import { createMovieListingColumns } from "./createMovieListingColumns";
import { MovieItem } from "./MovieItem";
import { useMovieListing } from "./useMovieListing";
import { sourceOptions, useMovieListingFilters } from "./useMovieListingFilters";

interface MovieListingProps {
    contentType: MovieContentSubtype;
}

export const MovieListing: FunctionComponent<MovieListingProps> = (props: MovieListingProps) => {
    const { contentType } = props;

    const {
        filters,
        setSort,
        setPage,
        setPageSize,
        setTitle,
        setIncludeMerged,
        setSources,
        clearFilters
    } = useMovieListingFilters();
    const {
        items,
        totalItems,
        isListLoading,
        moviesToMerge,
        getData,
        getDashboardHref,
        setItems,
        removeMovieFromMergeList,
        updateListAfterMerge,
        clearMoviesToMerge,
        addMovieItemToMergeList
    } = useMovieListing();

    const columns = useMemo(
        () =>
            createMovieListingColumns({
                contentType,
                movieListingItems: items,
                moviesToMerge,
                addMovieItemToMergeList
            }),
        [addMovieItemToMergeList, items, moviesToMerge, getDashboardHref]
    );

    useEffect(() => {
        getData(filters, contentType);
    }, [filters]);

    const handleMergedCheckboxChange = (
        _ev?: React.FormEvent<HTMLElement | HTMLInputElement> | undefined,
        checked?: boolean | undefined
    ) => {
        setIncludeMerged(checked ?? false);
    };

    const addNewMovieButtonProps: ButtonRenderProps<ButtonProps> = {
        render: () => AddMovieCommandBarButton({ contentType, items, pageSize: filters.pageSize, setItems })
    };

    const mergeMoviesButtonProps: ButtonRenderProps<ButtonProps> = {
        render: () => MergeMoviesCommandBarButton({ moviesToMerge, clearMoviesToMerge, updateListAfterMerge })
    };

    const renderRow = (rowProps?: DetailsRowProps, renderDefault?: RenderFunction<DetailsRowProps>) => {
        if (isRowMergable(moviesToMerge, rowProps?.item) && renderDefault) {
            return renderDefault(rowProps, renderDefault);
        }

        return renderNotMergableRow(rowProps, renderDefault);
    };

    return (
        <>
            <PageHeader>
                <HeaderBreadcrumb routes={contentManageRoutes} />
            </PageHeader>
            <ContentSection
                header={resolveListingHeader(contentType)}
                buttons={[mergeMoviesButtonProps, addNewMovieButtonProps]}
            >
                <Grid>
                    <GridRow>
                        <TagElementsList
                            items={moviesToMerge}
                            onItemDismiss={removeMovieFromMergeList}
                            renderItemText={renderMovieText}
                            styles={{ root: { paddingBottom: 10 } }}
                        />
                    </GridRow>
                    <GridRow>
                        <GridColumn sm={4}>
                            <SearchBox placeholder="Title" onChange={setTitle} value={filters.title} />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <TableFilter>
                                <DropdownMultiselect
                                    testId="movies-filters-sources"
                                    placeholder="Source"
                                    selectedKeys={filters.sources}
                                    onChange={setSources}
                                    options={sourceOptions}
                                />
                            </TableFilter>
                        </GridColumn>
                        <GridColumn sm={2}>
                            <TableFilter>
                                <Checkbox
                                    label="Include Merge Sources"
                                    onChange={handleMergedCheckboxChange}
                                    checked={filters.includeMerged ?? false}
                                />
                            </TableFilter>
                        </GridColumn>
                        <GridColumn sm={4}>
                            <Stack horizontalAlign="end">
                                <ClearFiltersButton onClear={clearFilters} isLabeled={true} />
                            </Stack>
                        </GridColumn>
                    </GridRow>
                    <GridRow>
                        <GridColumn sm={12}>
                            <ListTable
                                columns={columns}
                                items={items}
                                pagination={{
                                    ...filters,
                                    totalItems,
                                    onPageChange: setPage,
                                    onPageSizeChange: setPageSize
                                }}
                                sort={{ by: filters.sortBy, order: filters.sortOrder }}
                                onSortChange={setSort}
                                isLoading={isListLoading}
                                onRenderRow={moviesToMerge.length ? renderRow : undefined}
                            />
                        </GridColumn>
                    </GridRow>
                </Grid>
            </ContentSection>
        </>
    );
};

const renderMovieText = (movie: MovieItem) => {
    return movie.title;
};

const isRowMergable = (moviesToMerge: MovieItem[], rowItem: MovieItem) => {
    const movieToMerge = moviesToMerge[0];

    return (
        (moviesToMerge.some((movie: MovieItem) => movie.id === rowItem.id) ||
            (moviesToMerge.length < 2 && onlyOneMovieHasBuzzId(movieToMerge, rowItem))) &&
        !rowItem.merged
    );
};

const onlyOneMovieHasBuzzId = (movie1: MovieItem, movie2: MovieItem) => {
    return !movie1.buzz?.id !== !movie2.buzz?.id;
};

const resolveListingHeader = (movieContentType: MovieContentSubtype) => {
    switch (movieContentType) {
        case ContentSubtype.Movie:
            return "Movies";

        case ContentSubtype.TvSingle:
            return "TV Singles";

        case ContentSubtype.FeatureDocumentary:
            return "Feature Documentaries";

        case ContentSubtype.ShortFilm:
            return "Short Films";

        default:
            throw new Error(`${movieContentType} is not movie content type.`);
    }
};
